import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, ArrowUpRight, Sun, Home, Users, Calendar, ThermometerSnowflake, Caravan } from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { MobileBookingBar } from "@/components/site/MobileBookingBar";
import { Reveal } from "@/components/site/Reveal";
import { PHOTO_SIZES, PHOTOS } from "@/components/site/images";
import {
  ACTIVITIES_PATH,
  ADDRESS_ONE_LINE,
  BOOKING_URL,
  BUSINESS,
  CAMPGROUND_ID,
  CONTACT_PATH,
  MAPS_URL,
  ROOMS_PATH,
  WINTER_TEXANS_GUIDE_PATH,
  routeTo,
} from "@/lib/site";

const TITLE = "Winter Texan Stays in Zapata, TX | Long-Term Winter RV Park";
const DESCRIPTION =
  "Discover why Winter Texans choose Zapata for long-term RV stays. Mild South Texas weather, full-hookup sites, and a friendly community await.";
const CANONICAL = `https://amigoinn-rvpark.com${WINTER_TEXANS_GUIDE_PATH}`;
const OG_IMAGE = `https://amigoinn-rvpark.com${PHOTOS.cabinRow.src}`;

const WHY_WINTER = [
  {
    icon: ThermometerSnowflake,
    title: "Mild South Texas Winters",
    body:
      "Zapata sits in the warmest corner of Texas. Daytime temperatures from December through February often reach the 60s and 70s, with far fewer hard freezes than Hill Country or the coast. That means more mornings outside, fewer days bundled up, and lower heating costs.",
  },
  {
    icon: Sun,
    title: "Sunshine and Outdoor Living",
    body:
      "Winter days here are bright and dry enough for fishing, barbecues, walking, and afternoon gatherings. Guests can enjoy the covered picnic area, outdoor seating, and pet area without fighting snow or ice.",
  },
  {
    icon: Users,
    title: "A Friendly Community",
    body:
      "Winter Texans often return year after year. The clubhouse, potlucks, card games, and holiday gatherings create a relaxed social scene where guests quickly become neighbors and friends.",
  },
  {
    icon: Calendar,
    title: "Seasonal Events and Local Culture",
    body:
      "From community meals to holiday celebrations, the park stays active in winter. Nearby Zapata offers local restaurants, small-town events, and easy access to Falcon Lake for fishing or a scenic drive.",
  },
];

const AMENITIES = [
  {
    icon: Caravan,
    title: "Full-Hookup RV Sites",
    body:
      "Pull-through and back-in RV sites with full hookups let guests settle in for weeks or months without worrying about utilities, tanks, or power. Covered boat and RV storage is also available.",
  },
  {
    icon: Home,
    title: "Furnished Cabins and Park Models",
    body:
      "Not traveling in an RV? Cabins and park model units provide a furnished, move-in-ready option for a single winter or an entire season. Contact the office for current availability and extended-stay rates.",
  },
  {
    icon: Users,
    title: "Clubhouse and Recreation Hall",
    body:
      "The clubhouse is the center of winter life at the park: shuffleboard, card tables, a poker table, dartboard, board games, and space for group meals and celebrations.",
  },
  {
    icon: Sun,
    title: "Outdoor Gathering Spaces",
    body:
      "Covered picnic areas, barbecue pits, and outdoor seating give guests room to cook, dine, and relax together on warm winter afternoons.",
  },
];

const FAQ = [
  {
    q: "What is a Winter Texan?",
    a:
      "Winter Texan is a common South Texas term for retirees and seasonal visitors from northern states or Canada who spend the coldest months in Texas, enjoying the milder climate and friendly RV communities.",
  },
  {
    q: "Is Zapata a good place for Winter Texans?",
    a:
      "Yes. Zapata offers warm winter weather, lower costs than many coastal snowbird destinations, a tight-knit community, and easy access to Falcon Lake and the Rio Grande Valley. It is a relaxed alternative to busier snowbird corridors.",
  },
  {
    q: "Does Amigo Inn & RV Park offer long-term winter RV stays?",
    a:
      "Amigo Inn welcomes long-term and seasonal guests. Full-hookup RV sites, furnished cabins, park models, and extended-stay rates may be available. Contact the office to discuss your dates and needs.",
  },
  {
    q: "What amenities are open during winter?",
    a:
      "The clubhouse, game room, covered picnic area, barbecue pits, and Wi-Fi remain available for guests throughout the winter. Community gatherings are common during the peak season.",
  },
  {
    q: "How do I reserve a long-term winter stay?",
    a:
      "Start with the online availability checker or call the office at 956-765-3095. Winter spots fill up, so early reservations are recommended, especially for December through February.",
  },
];

export const Route = createFileRoute("/activities_/winter-texans-guide")({
  component: WinterTexansGuidePage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: CANONICAL },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Winter Texan Stays in Zapata, TX: Long-Term Winter RV Stays at Amigo Inn & RV Park",
          description: DESCRIPTION,
          url: CANONICAL,
          image: OG_IMAGE,
          author: { "@type": "Organization", name: BUSINESS.name, url: BUSINESS.siteUrl },
          publisher: { "@type": "Organization", name: BUSINESS.name, url: BUSINESS.siteUrl },
          about: { "@id": CAMPGROUND_ID },
          isPartOf: { "@type": "WebSite", name: BUSINESS.name, url: BUSINESS.siteUrl },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: BUSINESS.siteUrl },
            {
              "@type": "ListItem",
              position: 2,
              name: "Activities",
              item: `https://amigoinn-rvpark.com${ACTIVITIES_PATH}`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Winter Texan Stays in Zapata",
              item: CANONICAL,
            },
          ],
        }),
      },
    ],
  }),
});

function WinterTexansGuidePage() {
  const hero = PHOTOS.cabinRow;

  return (
    <div className="min-h-dvh bg-background">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-sm focus:bg-surface focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-forest"
      >
        Skip to content
      </a>

      <SiteHeader />

      <main id="main">
        {/* Hero */}
        <section className="relative isolate overflow-hidden bg-forest-deep py-24 sm:py-28 lg:py-32">
          <img
            src={hero.src}
            srcSet={hero.srcSet}
            sizes={PHOTO_SIZES.fullBleed}
            alt=""
            width={hero.width}
            height={hero.height}
            className="absolute inset-0 -z-10 size-full object-cover"
            aria-hidden="true"
          />
          <div
            className="absolute inset-0 -z-10 bg-[linear-gradient(105deg,oklch(0.259_0.042_163.6/0.94)_0%,oklch(0.259_0.042_163.6/0.8)_50%,oklch(0.259_0.042_163.6/0.6)_100%)]"
            aria-hidden="true"
          />
          <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
            <Reveal className="max-w-3xl text-cream">
              <p className="eyebrow text-sand">Long-Term Winter Stays in Zapata</p>
              <h1 className="mt-5 text-balance font-display text-3xl leading-[1.08] sm:text-4xl lg:text-[3.25rem]">
                Winter Texan Stays in Zapata, Texas
              </h1>
              <p className="mt-6 text-base leading-relaxed text-cream/85 sm:text-lg">
                Every year, seasonal visitors from the north trade snow and cold for the warm
                sunshine of South Texas. Zapata welcomes Winter Texans with mild weather, a relaxed
                pace, and a friendly community that makes Amigo Inn &amp; RV Park feel like home for
                the season.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4">
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-underline text-sm font-semibold text-cream"
                >
                  Check Winter Availability <ArrowUpRight className="size-4" aria-hidden="true" />
                </a>
                <a
                  href={MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-underline text-sm font-semibold text-sand"
                >
                  Get Directions <ArrowUpRight className="size-4" aria-hidden="true" />
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Why Zapata in winter */}
        <section className="bg-background py-20 sm:py-24" aria-labelledby="why-winter-title">
          <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
            <Reveal className="max-w-2xl">
              <p className="eyebrow text-clay">Why Winter Texans Love Zapata</p>
              <h2
                id="why-winter-title"
                className="mt-5 text-balance font-display text-3xl leading-[1.12] text-forest-deep sm:text-4xl"
              >
                A Warm, Affordable Winter Destination
              </h2>
              <p className="mt-5 text-base leading-relaxed text-forest-deep/80 sm:text-lg">
                Amigo Inn &amp; RV Park is at {ADDRESS_ONE_LINE}, a short drive from Falcon Lake and
                the heart of Zapata. For seasonal guests, the combination of climate, community,
                and value makes this corner of South Texas hard to beat.
              </p>
            </Reveal>

            <div className="mt-12 grid gap-6 sm:grid-cols-2">
              {WHY_WINTER.map((item, i) => (
                <Reveal
                  key={item.title}
                  delay={i * 0.06}
                  className="rounded-sm border border-forest/15 bg-surface p-7"
                >
                  <item.icon className="size-6 text-clay" aria-hidden="true" />
                  <h3 className="mt-4 font-display text-xl text-forest-deep">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-forest-deep/75">{item.body}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Long-term amenities */}
        <section className="bg-surface py-20 sm:py-24" aria-labelledby="amenities-title">
          <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
            <Reveal className="max-w-2xl">
              <p className="eyebrow text-clay">Stay a Month or the Whole Season</p>
              <h2
                id="amenities-title"
                className="mt-5 text-balance font-display text-3xl leading-[1.12] text-forest-deep sm:text-4xl"
              >
                Long-Term Amenities for Winter Guests
              </h2>
            </Reveal>

            <div className="mt-12 grid gap-6 sm:grid-cols-2">
              {AMENITIES.map((item, i) => (
                <Reveal
                  key={item.title}
                  delay={i * 0.06}
                  className="rounded-sm border border-forest/15 bg-background p-7"
                >
                  <item.icon className="size-6 text-clay" aria-hidden="true" />
                  <h3 className="mt-4 font-display text-xl text-forest-deep">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-forest-deep/75">{item.body}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Local winter scene */}
        <section className="bg-forest-deep py-20 text-cream sm:py-24" aria-labelledby="local-title">
          <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
            <div className="grid gap-12 lg:grid-cols-2 lg:items-center lg:gap-20">
              <Reveal className="max-w-2xl">
                <p className="eyebrow text-sand">Local Life in Winter</p>
                <h2
                  id="local-title"
                  className="mt-5 text-balance font-display text-3xl leading-[1.12] sm:text-4xl"
                >
                  Community, Culture and Falcon Lake
                </h2>
                <div className="mt-6 space-y-4 text-base leading-relaxed text-cream/85">
                  <p>
                    Winter in Zapata is not about staying indoors. It is about morning coffee in the
                    sunshine, potlucks with neighbors, afternoon card games, and weekend trips to
                    Falcon Lake. The town has a relaxed rhythm, local restaurants serving South
                    Texas and Mexican favorites, and a community that genuinely welcomes seasonal
                    guests.
                  </p>
                  <p>
                    For active guests, Falcon Lake stays fishable year-round thanks to mild South
                    Texas temperatures. Birding, scenic driving, and exploring the borderland
                    landscape are also popular winter pastimes.
                  </p>
                  <p>
                    Whether you want a quiet month to read and relax or a full season of social
                    gatherings, Zapata gives you room to build the winter routine that fits you.
                  </p>
                </div>
                <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4">
                  <Link
                    to={routeTo(ROOMS_PATH)}
                    className="link-underline text-sm font-semibold text-cream"
                  >
                    Rooms &amp; RV Sites <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                  <Link
                    to={routeTo(ACTIVITIES_PATH)}
                    className="link-underline text-sm font-semibold text-sand"
                  >
                    All Activities <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                </div>
              </Reveal>

              <Reveal x={16} className="space-y-8">
                {FAQ.map((item) => (
                  <div key={item.q} className="border-t border-cream/15 pt-6">
                    <h3 className="font-display text-xl text-cream">{item.q}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-cream/75">{item.a}</p>
                  </div>
                ))}
              </Reveal>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-background py-20 sm:py-24" aria-labelledby="plan-title">
          <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
            <Reveal className="max-w-2xl">
              <p className="eyebrow text-clay">Plan Your Winter Stay</p>
              <h2
                id="plan-title"
                className="mt-5 text-balance font-display text-3xl leading-[1.12] text-forest-deep sm:text-4xl"
              >
                Reserve Your Spot for the Season
              </h2>
              <p className="mt-5 text-base leading-relaxed text-forest-deep/80 sm:text-lg">
                The best Winter Texan spots fill up early. Contact Amigo Inn &amp; RV Park to ask
                about long-term rates, availability, and which accommodation fits your winter
                plans.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4">
                <Link
                  to={routeTo(CONTACT_PATH)}
                  className="link-underline text-sm font-semibold text-forest"
                >
                  Contact Us <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-underline text-sm font-semibold text-clay"
                >
                  Check Availability <ArrowUpRight className="size-4" aria-hidden="true" />
                </a>
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      <SiteFooter
        newsletterHeading="Winter Texan Updates & Park News"
        newsletterDescription="Get seasonal rates, long-term stay news, and community updates from Amigo Inn & RV Park."
      />
      <MobileBookingBar />
    </div>
  );
}
